import { AlertTriangle, CheckCircle2, CircleHelp, Loader, RefreshCw } from 'lucide-react'

import { Button, SelectField, TextField } from '@krp/ui-kit'

import type { VatEditorVm, VatProblemVm, VatReportVm, VatSourceActions, VatSourceVm } from '../model/types'

const number = (value: number) => value.toLocaleString('ru-RU')

/** Сводка одного источника и, если право есть, массовая установка ставки: выбор → предпросмотр → подтверждение числом → отчёт. */
export function VatSourceCard({ vm, actions }: { vm: VatSourceVm; actions: VatSourceActions }) {
  const headingId = `platform-vat-source-${vm.id}`
  return (
    <article className="platform-vat__card" aria-labelledby={headingId}>
      <header className="platform-vat__card-head">
        <h3 id={headingId}>{vm.title}</h3>
        {vm.badge !== null && <span className="platform-vat__badge">{vm.badge}</span>}
        <Button
          variant="ghost"
          icon={<RefreshCw size={16} aria-hidden="true" />}
          aria-label={`Обновить сводку: ${vm.title}`}
          busy={vm.status === 'loading'}
          onClick={actions.reload}
          actionId="platform.nomenclature-settings.source-reload"
        >
          Обновить
        </Button>
      </header>

      <Summary vm={vm} />
      {vm.note !== null && <p className="platform-vat__note">{vm.note}</p>}
      {vm.editor !== null && vm.status === 'ready' && <Editor vm={vm} editor={vm.editor} actions={actions} />}
    </article>
  )
}

function Summary({ vm }: { vm: VatSourceVm }) {
  if (vm.status === 'loading') {
    return (
      <p className="platform-vat__state" role="status">
        <Loader size={16} aria-hidden="true" /> Считаем…
      </p>
    )
  }
  if (vm.status === 'error') {
    return (
      <p className="platform-vat__state" data-kind="error" role="alert">
        <AlertTriangle size={16} aria-hidden="true" /> Не удалось получить сводку{vm.message !== null ? `: ${vm.message}` : '.'}
      </p>
    )
  }
  if (vm.status === 'no-data') {
    return (
      <div className="platform-vat__state" data-kind="no-data">
        <p>
          <CircleHelp size={16} aria-hidden="true" /> <strong>Нет данных о ставке НДС.</strong>
        </p>
        {vm.message !== null && <p>{vm.message}</p>}
        {vm.total !== null && <p className="platform-vat__muted">Товаров всего: {number(vm.total)}</p>}
      </div>
    )
  }
  return (
    <>
      <dl className="platform-vat__counts">
        <div>
          <dt>Товаров всего</dt>
          <dd>{vm.total === null ? '—' : number(vm.total)}</dd>
        </div>
        <div>
          <dt>Со ставкой</dt>
          <dd>{vm.withVat === null ? '—' : number(vm.withVat)}</dd>
        </div>
        <div data-warn={vm.withoutVat !== null && vm.withoutVat > 0 ? 'true' : undefined}>
          <dt>
            {vm.withoutVat !== null && vm.withoutVat > 0 && <AlertTriangle size={14} aria-hidden="true" />} Без ставки
            {vm.withoutVat !== null && vm.withoutVat > 0 && <span className="platform-vat__sr"> (продать такие товары нельзя)</span>}
          </dt>
          <dd>{vm.withoutVat === null ? '—' : number(vm.withoutVat)}</dd>
        </div>
      </dl>
      {vm.rates.length > 0 && (
        <ul className="platform-vat__rates" aria-label={`Ставки у товаров: ${vm.title}`}>
          {vm.rates.map((rate) => (
            <li key={rate.key}>
              <span>{rate.label}</span> <strong>{number(rate.count)}</strong>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function Editor({ vm, editor, actions }: { vm: VatSourceVm; editor: VatEditorVm; actions: VatSourceActions }) {
  const idle = editor.stage === 'idle'
  return (
    <div className="platform-vat__editor">
      <h4>Задать ставку массово</h4>
      <p className="platform-vat__muted">
        Запись меняет карточки товаров в учётной системе и откатывается с трудом. Сначала будет показано, что изменится, и ничего не
        запишется, пока вы не подтвердите число.
      </p>

      {(idle || editor.stage === 'previewing') && (
        <div className="platform-vat__choose">
          <SelectField
            label="Ставка"
            value={editor.rateCode}
            options={[{ value: '', label: 'Выберите ставку' }, ...editor.rateOptions]}
            onChange={actions.rateChanged}
            disabled={editor.stage === 'previewing'}
            actionId="platform.nomenclature-settings.rate"
          />
          <SelectField
            label="Какие товары"
            value={editor.groupKey}
            options={editor.groupOptions}
            onChange={actions.groupChanged}
            disabled={editor.stage === 'previewing'}
            actionId="platform.nomenclature-settings.group"
          />
          <Button
            variant="primary"
            busy={editor.stage === 'previewing'}
            disabled={editor.rateCode === ''}
            disabledReason={editor.rateCode === '' ? 'Сначала выберите ставку.' : null}
            onClick={actions.preview}
            actionId="platform.nomenclature-settings.preview"
          >
            Показать, что изменится
          </Button>
        </div>
      )}

      {editor.error !== null && (
        <p className="platform-vat__state" data-kind="error" role="alert">
          <AlertTriangle size={16} aria-hidden="true" /> {editor.error}
        </p>
      )}

      {editor.stage === 'preview' && editor.preview !== null && <Preview vm={vm} editor={editor} actions={actions} />}

      {editor.stage === 'running' && editor.progress !== null && (
        <p className="platform-vat__state" role="status">
          <Loader size={16} aria-hidden="true" /> Записываем в учётную систему: обработано {number(editor.progress.done)} из{' '}
          {number(editor.progress.total)}. Не закрывайте вкладку.
        </p>
      )}

      {editor.stage === 'done' && editor.report !== null && <Report report={editor.report} actions={actions} />}
    </div>
  )
}

function Preview({ vm, editor, actions }: { vm: VatSourceVm; editor: VatEditorVm; actions: VatSourceActions }) {
  const preview = editor.preview
  if (preview === null) return null
  const nothing = preview.willChange === 0
  return (
    <section className="platform-vat__preview" aria-label="Предпросмотр">
      <p>
        Ставка <strong>{preview.rateLabel}</strong> · {preview.scopeLabel}
      </p>
      <ul>
        <li>
          Будет изменено: <strong>{number(preview.willChange)}</strong>
        </li>
        <li>
          Уже стоит эта ставка: <strong>{number(preview.alreadySet)}</strong>
        </li>
        {preview.skipped.map((skip) => (
          <li key={skip.text}>
            Пропущено: <strong>{number(skip.count)}</strong> — {skip.text}
          </li>
        ))}
      </ul>
      {nothing ? (
        <p className="platform-vat__muted">Менять нечего: у всех выбранных товаров уже эта ставка.</p>
      ) : (
        <TextField
          label={`Чтобы записать, наберите число изменяемых товаров: ${number(preview.willChange)}`}
          value={editor.confirmText}
          onChange={actions.confirmChanged}
        />
      )}
      <div className="platform-vat__buttons">
        <Button
          variant="danger"
          disabled={nothing || !editor.confirmOk}
          disabledReason={nothing ? 'Менять нечего.' : editor.confirmOk ? null : 'Наберите число изменяемых товаров, чтобы подтвердить.'}
          onClick={actions.apply}
          actionId="platform.nomenclature-settings.apply"
          aria-label={`Записать ставку в ${vm.title}`}
        >
          Записать в {vm.badge ?? 'источник'}
        </Button>
        <Button variant="secondary" onClick={actions.cancel} actionId="platform.nomenclature-settings.cancel">
          Отмена
        </Button>
      </div>
    </section>
  )
}

function Report({ report, actions }: { report: VatReportVm; actions: VatSourceActions }) {
  return (
    <section className="platform-vat__report" aria-label="Отчёт о записи" data-complete={report.complete ? 'true' : 'false'}>
      <p className="platform-vat__state" data-kind={report.complete ? 'ok' : 'error'} role="status">
        {report.complete ? <CheckCircle2 size={16} aria-hidden="true" /> : <AlertTriangle size={16} aria-hidden="true" />}
        {report.complete ? ' Готово: все товары обработаны.' : ' Запись выполнена не полностью.'}
      </p>
      {report.stoppedBecause !== null && <p>{report.stoppedBecause}</p>}
      <ul>
        <li>
          Записано: <strong>{number(report.changed)}</strong>
        </li>
        <li>
          Уже стояла эта ставка: <strong>{number(report.alreadySet)}</strong>
        </li>
        {report.failed > 0 && (
          <li>
            Не записано (ошибка): <strong>{number(report.failed)}</strong>
          </li>
        )}
        {report.unknown > 0 && (
          <li>
            Исход неизвестен: <strong>{number(report.unknown)}</strong> — проверьте эти товары в 1С
          </li>
        )}
        {report.notProcessed > 0 && (
          <li>
            Не обработано: <strong>{number(report.notProcessed)}</strong>
          </li>
        )}
      </ul>
      {report.problems.length > 0 && <Problems problems={report.problems} />}
      <Button variant="secondary" onClick={actions.close} actionId="platform.nomenclature-settings.close">
        Закрыть отчёт
      </Button>
    </section>
  )
}

function Problems({ problems }: { problems: readonly VatProblemVm[] }) {
  return (
    <div className="platform-vat__problems">
      <table>
        <thead>
          <tr>
            <th scope="col">Товар</th>
            <th scope="col">Что с ним</th>
            <th scope="col">Ответ 1С</th>
          </tr>
        </thead>
        <tbody>
          {problems.map((problem) => (
            <tr key={problem.id}>
              <td data-label="Товар">
                {problem.name ?? 'Наименование не получено'}
                {problem.article !== null && <span className="platform-vat__muted"> · арт. {problem.article}</span>}
              </td>
              <td data-label="Что с ним">{problem.state}</td>
              <td data-label="Ответ 1С">{problem.message ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
